import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Subject, takeUntil } from 'rxjs';
import { FNFResponse } from '../../../../../models/response';
import { IntegrationService } from '../../../../../services/integration.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-square-integration',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    CommonModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
  ],
  templateUrl: './square-integration.component.html',
  styleUrl: './square-integration.component.css',
})
export class SquareIntegrationComponent implements OnInit, OnDestroy {
  integrationService = inject(IntegrationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  readonly dialogRef = inject(MatDialogRef<SquareIntegrationComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);

  modules: any = [
    { name: 'Basic details', iconCheck: false, iconLoading: true, iconDot: false },
    { name: 'Types & Categories', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Customizations', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Menu items', iconCheck: false, iconLoading: false, iconDot: true },
    { name: 'Finishing setup', iconCheck: false, iconLoading: false, iconDot: true },
  ];

  squareConnectStatus = '';
  connectionLoading = false;
  confirmConnect = true;
  continueConnect = false;
  successConnect = false;
  errorConnect = false;
  errorAuthorization = false;
  menuSelection = false;
  squareLocation = false;
  selectedMenuLength: any;
  isLocationSelected = false;
  squareMenus: any = [];
  squareLocations: any = [];
  selectedLocation: any;

  /** Set when this dialog is opened to force a resync rather than a
   * voluntary connect (see SquareResyncService) — hides every Cancel
   * action so the flow can't be dismissed. */
  forceResync = false;

  private destroy$ = new Subject<void>();

  ngOnInit() {
    if (this.data?.squareAuthDetails?.status) {
      this.squareConnectStatus = this.data.squareAuthDetails.status;
    }
    this.forceResync = !!this.data?.forceResync;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  connectSquare() {
    if (this.data?.squareAuthDetails?.auth_url) {
      window.open(this.data.squareAuthDetails.auth_url, '_blank');
      this.confirmConnect = false;
      this.connectionLoading = true;
      this.errorConnect = false;
      this.successConnect = false;
      this.errorAuthorization = false;
      this.menuSelection = false;
      this.squareConnectStatus = 'CONNECTING';
      this.ngxSpinnerService.show('squareSpinner');
      this.getSquareConnectionStatus();
    }
  }

  getSquareConnectionStatus() {
    this.integrationService
      .getSquareConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            return;
          }
          this.squareConnectStatus = response.data.status;
          if (response.data.status === 'AUTHORIZED') {
            setTimeout(() => {
              // this.getSquarMenus();
              this.getSquarLocations();
            }, 1000);
          } else if (response.data.status === 'AUTHORIZATION_FAILED') {
            this.displayAuthorizationError();
          } else if (response.data.status === 'COMPLETED') {
            this.modules[4].iconCheck = true;
            this.modules[4].iconLoading = false;
            this.modules[4].iconDot = false;
            this.displayConnectionSuccess();
          } else if (response.data.status === 'FAILED') {
            this.displayConnectionError();
          } else {
            setTimeout(() => {
              this.getSquareConnectionStatus();
            }, 3000);
          }
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  getSquarLocations() {
    this.integrationService.getSquareLocations().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.displayConnectionError();
          return;
        } else if (!response.data) {
          this.displayConnectionError();
          return;
        } else if (response.data?.locations && response.data?.locations.length == 1) {
          this.getSquarMenus();
        } else if (response.data?.locations && response.data?.locations.length > 0) {
          response.data.locations.forEach((e: any) => {
            this.squareLocations.push({ id: e.id, name: e.name, isChecked: false });
          });
          this.confirmConnect = false;
          this.connectionLoading = false;
          this.continueConnect = false;
          this.errorConnect = false;
          this.successConnect = false;
          this.errorAuthorization = false;
          this.menuSelection = false;
          this.squareLocation = true;
        }
      },
      error: () => {
        this.displayConnectionError();
      },
    });
  }

  getSquarMenus() {
    this.squareLocation = false;
    this.integrationService.getSquareMenus().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.displayConnectionError();
          return;
        } else if (!response.data) {
          this.displayConnectionError();
          return;
        } else if (response.data?.menus && response.data?.menus.length == 1) {
          this.continueSquareMigration();
        } else if (response.data?.menus && response.data?.menus.length > 0) {
          response.data.menus.forEach((e: any) => {
            this.squareMenus.push({ id: e.id, name: e.name, isChecked: false });
          });
          this.confirmConnect = false;
          this.connectionLoading = false;
          this.continueConnect = false;
          this.errorConnect = false;
          this.successConnect = false;
          this.errorAuthorization = false;
          this.menuSelection = true;
        } else if (response.data?.menus && response.data?.menus.length == 0) {
          this.continueSquareMigration();
        }
      },
      error: () => {
        this.displayConnectionError();
      },
    });
  }

  sendSquareSelectedLocation() {
    if (this.isLocationSelected) {
      let payload = { location_id: this.selectedLocation.id };
      this.integrationService.saveSelectedLocations(payload).subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.displayConnectionError();
            return;
          } else if (!response.data) {
            this.displayConnectionError();
            return;
          }
          this.getSquarMenus();
        },
        error: () => {
          this.displayConnectionError();
        },
      });
    }
  }

  sendSquareSelectedMenus() {
    let filterMenus = this.squareMenus.filter((f: any) => f.isChecked);
    if (filterMenus.length > 0) {
      let menuIds = filterMenus.map((f: any) => f.id);
      let payload = { menu_ids: menuIds };
      this.integrationService.saveSelectedMenus(payload).subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.displayConnectionError();
            return;
          } else if (!response.data) {
            this.displayConnectionError();
            return;
          }
          this.continueSquareMigration();
        },
        error: () => {
          this.displayConnectionError();
        },
      });
    } else {
    }
  }

  continueSquareMigration() {
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = true;
    this.errorConnect = false;
    this.successConnect = false;
    this.errorAuthorization = false;
    this.menuSelection = false;

    setTimeout(() => {
      this.modules[0].iconCheck = true;
      this.modules[0].iconLoading = false;
      this.modules[1].iconLoading = true;
      this.modules[1].iconDot = false;
    }, 1000);
    setTimeout(() => {
      this.modules[1].iconCheck = true;
      this.modules[1].iconLoading = false;
      this.modules[2].iconLoading = true;
      this.modules[2].iconDot = false;
    }, 2000);
    setTimeout(() => {
      this.modules[2].iconCheck = true;
      this.modules[2].iconLoading = false;
      this.modules[3].iconLoading = true;
      this.modules[3].iconDot = false;
    }, 15000);
    setTimeout(() => {
      this.modules[3].iconCheck = true;
      this.modules[3].iconLoading = false;
      this.modules[4].iconLoading = true;
      this.modules[4].iconDot = false;
    }, 20000);
    this.squareMigration();
  }

  squareMigration() {
    this.integrationService
      .squareMigration({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.displayConnectionError();
            return;
          } else if (!response.data) {
            this.displayConnectionError();
            return;
          }
          this.getSquareConnectionStatus();
        },
        error: () => {
          this.displayConnectionError();
        },
      });
  }

  displayConnectionSuccess() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = true;
    this.menuSelection = false;
  }

  displayConnectionError() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = true;
    this.successConnect = false;
    this.menuSelection = false;
  }

  displayAuthorizationError() {
    this.errorAuthorization = true;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = false;
    this.menuSelection = false;
  }

  finishSetup() {
    this.dialogRef.close('finishSetup');
  }

  selectMenu(event: any, menu: any) {
    let isCheck = event.target.checked;
    let findMenu = this.squareMenus.find((f: any) => f.id == menu.id);
    if (findMenu) {
      findMenu.isChecked = isCheck;
    }
    this.selectedMenuLength = this.squareMenus.filter((f: any) => f.isChecked)?.length;
  }

  selectLocation(event: any, location: any) {
    this.isLocationSelected = true;
    this.squareLocations.forEach((e: any) => {
      if (e.id == location.id) {
        e.isChecked = true;
        this.selectedLocation = e;
      } else {
        e.isChecked = false;
      }
    });
  }
}
